import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { SeoHead } from "@/components/common/SeoHead";
import { WhatsAppCTA } from "@/components/common/WhatsAppCTA";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Check, Plane, Users, Calendar, Globe } from "lucide-react";

const BREADCRUMB = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://tripsyfy.com" },
    { "@type": "ListItem", position: 2, name: "International Flights", item: "https://tripsyfy.com/international-flight-booking" },
    { "@type": "ListItem", position: 3, name: "Flights to Africa from USA", item: "https://tripsyfy.com/flights-to-africa-from-usa" },
  ],
});

const FAQ_SCHEMA = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Which African countries does Tripsyfy book flights to from the USA?", acceptedAnswer: { "@type": "Answer", text: "We help travelers book flights to destinations across Africa including Nigeria (Lagos, Abuja), Ghana (Accra), Kenya (Nairobi), South Africa (Johannesburg, Cape Town), Ethiopia (Addis Ababa), Tanzania (Dar es Salaam, Kilimanjaro), Senegal (Dakar), Cameroon, Uganda, and many more." } },
    { "@type": "Question", name: "Which airlines fly from the USA to Africa?", acceptedAnswer: { "@type": "Answer", text: "Ethiopian Airlines, Kenya Airways, Delta, and South African Airways operate direct or hub-connected routes. Emirates, Qatar Airways, Lufthansa, Air France, KLM, and Turkish Airlines offer one-stop connections via their Middle Eastern or European hubs. The best airline depends on your destination and US departure city." } },
    { "@type": "Question", name: "What is the cheapest time to fly from the USA to Africa?", acceptedAnswer: { "@type": "Answer", text: "Shoulder seasons (April–June and September–November) typically offer lower fares for West Africa and East Africa travel. December through January is peak season due to holiday travel. Booking 3–6 months in advance and traveling mid-week can also help reduce costs." } },
    { "@type": "Question", name: "Can Tripsyfy book group flights to Africa for family events?", acceptedAnswer: { "@type": "Answer", text: "Yes. We specialize in coordinating group flights to Africa for family reunions, weddings, funerals, and community events. We handle multiple travelers from different US cities, ensuring coordinated connections and arrivals." } },
    { "@type": "Question", name: "Does Tripsyfy help with connecting flights through Europe or the Middle East?", acceptedAnswer: { "@type": "Answer", text: "Yes. Most US-to-Africa routes involve a connecting flight through Europe (London, Paris, Amsterdam, Frankfurt) or the Middle East (Dubai, Doha, Abu Dhabi). We advise on the best connection based on layover time, cost, transit visa requirements, and reliability." } },
    { "@type": "Question", name: "How do I book flights to Africa from the USA with Tripsyfy?", acceptedAnswer: { "@type": "Answer", text: "Call us at +1 972-666-1122, message us on WhatsApp, or submit a quote request. Share your US departure city, African destination, travel dates, and number of travelers. We'll compare all available routing options and get back to you promptly." } },
  ],
});

const africaDestinations = [
  "Nigeria — Lagos (LOS), Abuja (ABV)", "Ghana — Accra (ACC)", "Kenya — Nairobi (NBO)",
  "South Africa — Johannesburg (JNB), Cape Town (CPT)", "Ethiopia — Addis Ababa (ADD)",
  "Tanzania — Dar es Salaam (DAR), Kilimanjaro (JRO)", "Senegal — Dakar (DSS)",
  "Uganda — Kampala / Entebbe (EBB)", "Cameroon — Yaoundé (NSI), Douala (DLA)",
  "Ivory Coast — Abidjan (ABJ)",
];

const whoItIsFor = [
  { icon: Users, title: "African diaspora families", desc: "Returning home for weddings, funerals, holidays, and family visits." },
  { icon: Plane, title: "Safari and adventure travelers", desc: "Kenya, Tanzania, South Africa — coordinated travel with game lodge stays." },
  { icon: Globe, title: "Business travelers", desc: "Frequent travel to West Africa and East Africa for business and trade." },
  { icon: Calendar, title: "Group and community travel", desc: "Church groups, community organizations, and family reunions to Africa." },
];

const whatWeHandle = [
  "Searching all major airlines and routing options to Africa",
  "Comparing Middle East vs. European hub connections",
  "Multi-city itineraries within Africa (e.g., Nairobi + Dar es Salaam)",
  "Group flight coordination for families and organizations",
  "Transit visa guidance for European and Middle East connections",
  "Flexible date searching to minimize fares",
  "Baggage allowance guidance per airline and route",
  "Seat coordination for families traveling together",
  "Booking and issuing confirmed e-tickets",
];

const faqs = [
  { q: "Which African countries does Tripsyfy book flights to from the USA?", a: "We help book flights to Nigeria, Ghana, Kenya, South Africa, Ethiopia, Tanzania, Senegal, Uganda, Cameroon, Ivory Coast, and many more African destinations." },
  { q: "Which airlines fly from the USA to Africa?", a: "Ethiopian Airlines, Kenya Airways, and Delta fly direct or hub-connected routes. Emirates, Qatar, Lufthansa, Air France, and KLM offer one-stop connections. The best option depends on your destination and departure city." },
  { q: "What is the cheapest time to fly from the USA to Africa?", a: "Shoulder seasons (April–June and September–November) typically offer lower fares. Book 3–6 months in advance and avoid the December holiday peak for the best rates." },
  { q: "Can Tripsyfy book group flights to Africa for family events?", a: "Yes. We specialize in coordinating group flights to Africa for weddings, funerals, reunions, and community events — including travelers departing from different US cities." },
  { q: "Does Tripsyfy help with connecting flights through Europe or the Middle East?", a: "Yes. We advise on the best connection hubs based on layover time, cost, transit visa requirements, and reliability — whether routing through Dubai, London, Amsterdam, or Addis Ababa." },
  { q: "How do I book flights to Africa from the USA with Tripsyfy?", a: "Call +1 972-666-1122, message us on WhatsApp, or submit a quote request with your US departure city, African destination, travel dates, and number of travelers." },
];

export default function FlightsToAfricaPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div id="main-content" className="min-h-screen bg-white">
      <Helmet>
        <script type="application/ld+json">{BREADCRUMB}</script>
        <script type="application/ld+json">{FAQ_SCHEMA}</script>
      </Helmet>
      <SeoHead
        title="Flights to Africa from USA"
        description="Tripsyfy helps travelers book flights to Africa from the USA — Nigeria, Ghana, Kenya, South Africa, and more — with human support for routing, family travel, and group bookings."
        canonical="https://tripsyfy.com/flights-to-africa-from-usa"
      />
      <Navbar />

      <section className="relative pt-28 pb-14 bg-primary">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <p className="text-sky-300 text-xs font-semibold tracking-widest uppercase mb-3">Africa Flight Specialist</p>
          <h1 className="heading-xl text-white mb-3">Flights to Africa from the USA</h1>
          <p className="text-base text-white/70 max-w-xl leading-relaxed mb-8">
            Tripsyfy helps US-based travelers book flights to destinations across Africa — with expert routing guidance, group coordination, and human support for routes that online tools handle poorly.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/contact"><Button className="bg-accent text-white hover:bg-accent/90">Request a Quote</Button></Link>
            <WhatsAppCTA variant="button" />
          </div>
        </div>
      </section>

      <section className="section-padding bg-surface-subtle">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="max-w-2xl mb-8">
              <p className="overline mb-3">African Destinations</p>
              <h2 className="heading-lg text-primary">Countries and cities we serve across Africa</h2>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {africaDestinations.map((dest, i) => (
              <AnimatedSection key={dest} delay={i * 0.04}>
                <div className="bg-white rounded-xl p-4 shadow-card flex items-center gap-3">
                  <Plane className="h-4 w-4 text-accent shrink-0" />
                  <span className="text-sm text-foreground">{dest}</span>
                </div>
              </AnimatedSection>
            ))}
          </div>
          <p className="text-sm text-muted-foreground mt-4">+ all other African airports via connections. Contact us for any destination.</p>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="max-w-2xl mb-10">
              <p className="overline mb-3">Who This Is For</p>
              <h2 className="heading-lg text-primary">Built for Africa-bound travelers from the US</h2>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {whoItIsFor.map(({ icon: Icon, title, desc }, i) => (
              <AnimatedSection key={title} delay={i * 0.06}>
                <div className="bg-surface-subtle rounded-xl p-5">
                  <div className="h-10 w-10 rounded-xl bg-accent/10 flex items-center justify-center mb-3">
                    <Icon className="h-5 w-5 text-accent" />
                  </div>
                  <p className="text-sm font-semibold text-primary mb-1">{title}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <AnimatedSection>
              <p className="overline mb-3">What We Handle</p>
              <h3 className="heading-md text-primary mb-5">Full flight support, start to finish</h3>
              <ul className="space-y-3">
                {whatWeHandle.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="h-5 w-5 rounded-full bg-accent/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-accent" />
                    </div>
                    <span className="text-sm text-foreground leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <div className="bg-surface-subtle rounded-xl p-7">
                <p className="overline mb-3">Get Started</p>
                <h3 className="heading-md text-primary mb-4">Book your Africa flight</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                  Share your US departure city, African destination, travel dates, and number of travelers — we'll compare all routing options and respond promptly.
                </p>
                <WhatsAppCTA variant="banner" className="mb-3" />
                <Link to="/contact">
                  <Button variant="outline" className="w-full mt-2">Submit a Quote Request</Button>
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="max-w-2xl mb-10">
              <p className="overline mb-3">How It Works</p>
              <h2 className="heading-lg text-primary">How we book your Africa flights</h2>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {[
              { num: "01", title: "Share your details", desc: "Tell us your US departure city, African destination, travel dates, and number of travelers. Multiple departure cities? No problem." },
              { num: "02", title: "We compare routes via Middle East or Europe", desc: "We evaluate connections through Dubai, Doha, London, Paris, Amsterdam, and Addis Ababa to find the best price and layover combination." },
              { num: "03", title: "You choose what works", desc: "We present your best options with clear pricing and transit details. You decide — with our honest guidance on each routing." },
              { num: "04", title: "We confirm and support", desc: "We handle the booking, send confirmed e-tickets, and remain available for any changes or support throughout your trip." },
            ].map(({ num, title, desc }, i) => (
              <AnimatedSection key={num} delay={i * 0.08}>
                <div>
                  <p className="text-4xl font-bold text-primary/10 mb-2 leading-none">{num}</p>
                  <h3 className="text-sm font-bold text-primary mb-2">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-surface-subtle">
        <div className="container-wide px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">
          <AnimatedSection>
            <p className="overline mb-3 text-center">Common Questions</p>
            <h2 className="heading-lg text-primary text-center mb-10">Flights to Africa from USA</h2>
          </AnimatedSection>
          <div className="space-y-3">
            {faqs.map(({ q, a }, i) => (
              <AnimatedSection key={i} delay={i * 0.04}>
                <details className="bg-white rounded-xl shadow-card overflow-hidden">
                  <summary className="px-6 py-4 cursor-pointer font-semibold text-primary text-sm list-none flex items-center justify-between gap-4 hover:text-accent transition-colors">
                    {q}<span className="text-accent text-lg leading-none shrink-0">+</span>
                  </summary>
                  <div className="px-6 pb-5">
                    <p className="text-sm text-muted-foreground leading-relaxed">{a}</p>
                  </div>
                </details>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-primary">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-center">
            <div className="lg:col-span-2">
              <AnimatedSection>
                <h2 className="heading-lg text-white mb-3">Ready to book your Africa flight?</h2>
                <p className="text-white/70 text-base leading-relaxed">Call, WhatsApp, or submit a quote — we respond within 24 hours.</p>
              </AnimatedSection>
            </div>
            <div className="flex flex-col gap-3 lg:items-end">
              <Link to="/contact"><Button className="bg-accent text-white hover:bg-accent/90 w-full lg:w-auto">Request a Quote</Button></Link>
              <WhatsAppCTA variant="button" />
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/10 flex flex-wrap gap-6 text-sm text-white/60">
            <Link to="/international-flight-booking" className="hover:text-white transition-colors">→ International Flight Booking</Link>
            <Link to="/flights-to-india-from-usa" className="hover:text-white transition-colors">→ Flights to India from USA</Link>
            <Link to="/group-travel-planning" className="hover:text-white transition-colors">→ Group Travel Planning</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
